// ============================================================================
// CLEO'S SECRET — Twelve-week timeline
// File: src/components/program/TwelveWeekTimeline.jsx
//
// Horizontal 12-block strip for the patient ProgramDashboard. Renders each
// week with state-driven styling: locked / current / completed /
// upcoming-milestone. Weeks 7 and 12 are visually distinct (taller, gold
// accent) regardless of state.
//
// Click on any non-locked block fires onWeekClick(weekNumber). The
// weekly-module route is Session 3 work; in Session 2 the handler resolves
// to onNavigate('weekly-module', { week: N }) which is a no-op until the
// route case is added in Session 3.
//
// Responsive: horizontal-scroll container on viewport < 1024px; full 12
// visible without scroll on >= 1024px.
// ============================================================================

import React from 'react';
import { P, FF } from '../../lib/design-tokens';

var PHASE_LABELS = {
  foundations: 'Foundations',
  recalibration: 'Recalibration',
  optimisation: 'Optimisation',
};

function CheckGlyph({ color }) {
  return (
    <svg width={12} height={12} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8.5 L7 12 L13 4" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WeekBlock({ module: mod, onClick }) {
  var state = mod.state;
  var weekNumber = mod.weekNumber;
  var isMilestone = weekNumber === 7 || weekNumber === 12;

  // State-driven styling
  var bg, borderColor, borderWidth, textColor, opacity, cursor, accentLeftColor;
  var clickable = false;

  if (state === 'locked') {
    bg = P.SAND; borderColor = P.SAND3; borderWidth = 1;
    textColor = P.TF; opacity = 0.55; cursor = 'not-allowed';
  } else if (state === 'current') {
    bg = P.PAP; borderColor = P.GO; borderWidth = 2;
    textColor = P.INK; opacity = 1; cursor = 'pointer';
    clickable = true;
  } else if (state === 'completed') {
    bg = P.PAP; borderColor = P.SAND3; borderWidth = 1;
    textColor = P.INK; opacity = 1; cursor = 'pointer';
    accentLeftColor = P.GREEN;
    clickable = true;
  } else { // upcoming-milestone
    bg = P.SAND2; borderColor = P.GO; borderWidth = 1;
    textColor = P.BR2; opacity = 0.85; cursor = 'not-allowed';
  }

  // Milestone weeks: gold accent border overrides default for non-locked,
  // and the block is taller regardless of state.
  if (isMilestone && state !== 'locked') {
    borderColor = P.GO;
    borderWidth = 2;
  }
  var blockMinHeight = isMilestone ? 'clamp(150px, 14vw, 180px)' : 'clamp(130px, 12vw, 156px)';

  return (
    <div
      onClick={clickable ? onClick : undefined}
      style={{
        flex: '0 0 clamp(110px, 9vw, 130px)',
        minHeight: blockMinHeight,
        padding: 'clamp(14px, 1.3vw, 18px) clamp(10px, 1vw, 14px)',
        background: bg,
        border: borderWidth + 'px solid ' + borderColor,
        borderLeft: accentLeftColor ? '3px solid ' + accentLeftColor : (borderWidth + 'px solid ' + borderColor),
        opacity: opacity,
        cursor: cursor,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        transition: 'border-color 0.15s, background 0.15s',
      }}
      onMouseEnter={clickable ? function(e) {
        e.currentTarget.style.borderColor = P.BR;
      } : undefined}
      onMouseLeave={clickable ? function(e) {
        e.currentTarget.style.borderColor = borderColor;
      } : undefined}
    >
      {/* "Today" badge — floats above current block */}
      {state === 'current' && (
        <div style={{
          position: 'absolute',
          top: -10,
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: FF,
          fontSize: 'clamp(9px, 0.78vw, 10px)',
          fontWeight: 700,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          padding: '3px 9px',
          background: P.GO,
          color: P.INK,
          border: '1px solid ' + P.GO,
          whiteSpace: 'nowrap',
        }}>Today</div>
      )}

      {/* Week label */}
      <div style={{
        fontFamily: FF,
        fontSize: 'clamp(9px, 0.78vw, 10px)',
        fontWeight: 700,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        color: state === 'locked' ? P.TF : P.BR2,
      }}>Week {String(weekNumber).padStart(2, '0')}</div>

      {/* Phase label */}
      <div style={{
        fontFamily: FF,
        fontSize: 'clamp(11px, 0.95vw, 12px)',
        fontWeight: 500,
        color: state === 'locked' ? P.TF : P.BR2,
        fontStyle: 'italic',
      }}>{PHASE_LABELS[mod.phase] || mod.phase}</div>

      {/* Title (truncated to 2 lines) */}
      <div style={{
        fontFamily: FF,
        fontSize: 'clamp(13px, 1.1vw, 15px)',
        fontWeight: 500,
        color: textColor,
        lineHeight: 1.3,
        flex: 1,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
      }}>{mod.title}</div>

      {/* State indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {state === 'completed' && <CheckGlyph color={P.GREEN} />}
        {state === 'current' && (
          <div style={{
            width: 8, height: 8, borderRadius: '50%', background: P.GO,
          }} />
        )}
        {state === 'upcoming-milestone' && (
          <div style={{
            width: 8, height: 8, background: P.GO,
            transform: 'rotate(45deg)',
          }} />
        )}
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(10px, 0.85vw, 11px)',
          fontWeight: 600,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: state === 'locked' ? P.TF : (state === 'completed' ? P.GREEN : P.BR2),
        }}>
          {state === 'completed' ? 'Done' :
           state === 'current' ? 'In progress' :
           state === 'upcoming-milestone' ? (mod.milestoneLabel || 'Milestone') :
           'Locked'}
        </div>
      </div>
    </div>
  );
}

export default function TwelveWeekTimeline(props) {
  var modules = props.modules || [];
  var onWeekClick = props.onWeekClick || function() {};

  return (
    <div style={{
      display: 'flex',
      gap: 'clamp(6px, 0.8vw, 10px)',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      paddingBottom: 12,
      paddingTop: 14,
    }}>
      {modules.map(function(m) {
        return (
          <WeekBlock
            key={m.weekNumber}
            module={m}
            onClick={function() { onWeekClick(m.weekNumber); }}
          />
        );
      })}
    </div>
  );
}
